import { useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeService } from '../../../services/KnowledgeService';
import type {
  DocumentListOptions,
  KnowledgeBaseDocument,
  WithResourceId,
} from '../../../types/knowledge';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';

export function useUploadKnowledgeDocumentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      file,
    }: {
      databaseId: string;
      file: File;
      listOptions?: DocumentListOptions;
    }) => knowledgeService.uploadDocument(databaseId, file),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.documentsRoot(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}

export function useDownloadKnowledgeDocumentMutation() {
  return useMutation({
    mutationFn: async ({
      databaseId,
      document,
    }: {
      databaseId: string;
      document: KnowledgeBaseDocument;
    }) => {
      if (!document.id) {
        throw new Error('Cannot download a document without id');
      }

      const blob = await knowledgeService.downloadDocument(
        databaseId,
        document.id,
      );
      const objectUrl = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = objectUrl;
      anchor.download =
        document.fileName ?? document.displayName ?? document.id;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    },
  });
}

export function useDeleteKnowledgeDocumentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      documentId,
    }: {
      databaseId: string;
      documentId: string;
      listOptions?: DocumentListOptions;
    }) => knowledgeService.deleteDocument(databaseId, documentId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.documentsRoot(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}

export function useResumeAbandonedDocumentEmbeddingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      documentId,
    }: {
      databaseId: string;
      documentId: string;
      listOptions?: DocumentListOptions;
    }) =>
      knowledgeService.resumeAbandonedDocumentEmbedding(databaseId, documentId),
    onSuccess: (document, variables) => {
      queryClient.setQueriesData<Array<WithResourceId<KnowledgeBaseDocument>>>(
        { queryKey: knowledgeQueryKeys.documentsRoot(variables.databaseId) },
        (current) => {
          if (!current) {
            return current;
          }

          return current.map((existing) =>
            existing.id === document.id ? document : existing,
          );
        },
      );
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.documentsRoot(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}
